import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, ShieldCheck, AlertTriangle, LifeBuoy, BookOpen } from "lucide-react";
import { getYedaConfig, askYeda, yedaFeedback } from "../lib/hub";

const LANGS = [{ c: "en", l: "EN" }, { c: "fr", l: "FR" }, { c: "ht", l: "HT" }, { c: "es", l: "ES" }];
const GREETING = {
  en: "Hi, I'm the YEDA Business Guide. Ask me about TAVEN memberships, onboarding, applications, or billing.",
  fr: "Bonjour, je suis le YEDA Business Guide. Posez vos questions sur les adhésions, l'inscription et la facturation TAVEN.",
  ht: "Bonjou, mwen se YEDA Business Guide. Poze m kesyon sou manm TAVEN, aplikasyon ak fakti.",
  es: "Hola, soy el YEDA Business Guide. Pregúntame sobre membresías, incorporación y facturación de TAVEN.",
};

const sessionId = (() => {
  let s = localStorage.getItem("yeda_session");
  if (!s) { s = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("yeda_session", s); }
  return s;
})();

export default function YedaGuide() {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [lang, setLang] = useState(localStorage.getItem("taven_lang") || "en");
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scroller = useRef(null);

  useEffect(() => { getYedaConfig().then(setCfg).catch(() => setCfg({ enabled: false })); }, []);
  useEffect(() => { if (open && msgs.length === 0) setMsgs([{ role: "guide", text: GREETING[lang] || GREETING.en }]); }, [open]); // eslint-disable-line
  useEffect(() => { scroller.current?.scrollTo(0, scroller.current.scrollHeight); }, [msgs, busy]);

  if (!cfg || !cfg.enabled) return null;

  const send = async () => {
    const q = input.trim(); if (!q || busy) return;
    setInput(""); setMsgs((m) => [...m, { role: "user", text: q }]); setBusy(true);
    try {
      const r = await askYeda({ question: q, language: lang, session_id: sessionId });
      setMsgs((m) => [...m, {
        role: "guide", text: r.answer, sources: r.sources || [], escalated: r.escalated,
        status: r.status, conversation_id: r.conversation_id,
      }]);
    } catch (e) {
      const rl = e?.response?.status === 429;
      setMsgs((m) => [...m, { role: "guide", text: rl ? "You're sending messages a bit fast — please wait a moment." : "Sorry, I couldn't respond just now. Please try again or contact TAVEN support." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed bottom-5 right-5 z-50 flex items-center gap-2 bg-taven-black text-white pl-4 pr-5 py-3 rounded-full shadow-lg hover:bg-taven-orange transition-colors" data-testid="yeda-launcher">
          <BookOpen className="w-5 h-5 text-taven-gold" /> <span className="text-sm font-bold">YEDA Business Guide</span>
        </button>
      )}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[600px] bg-white border border-black/15 shadow-2xl flex flex-col" data-testid="yeda-panel">
          <div className="flex items-center justify-between bg-taven-black text-white px-4 py-3">
            <div className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-taven-gold" /><span className="font-black font-display text-sm">YEDA Business Guide</span></div>
            <div className="flex items-center gap-2">
              <select value={lang} onChange={(e) => setLang(e.target.value)} className="bg-white/10 text-white text-xs px-1.5 py-1 rounded" data-testid="yeda-lang">
                {LANGS.map((l) => <option key={l.c} value={l.c} className="text-black">{l.l}</option>)}
              </select>
              <button onClick={() => setOpen(false)} data-testid="yeda-close"><X className="w-5 h-5" /></button>
            </div>
          </div>

          <div ref={scroller} className="flex-1 overflow-y-auto p-3 space-y-3 bg-taven-gray/30" data-testid="yeda-messages">
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "text-right" : ""}>
                <div className={`inline-block max-w-[85%] px-3 py-2 text-sm rounded-lg ${m.role === "user" ? "bg-taven-black text-white" : "bg-white border border-black/10"}`} data-testid={`yeda-msg-${m.role}`}>
                  {m.text}
                </div>
                {m.role === "guide" && m.sources?.length > 0 && (
                  <div className="mt-1.5 space-y-1" data-testid="yeda-sources">
                    {m.sources.map((s, j) => (
                      <div key={j} className="text-[11px] text-black/50 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3 text-taven-gold" /> {s.title} {s.version && s.version !== "current" ? `v${s.version}` : ""}
                        <span className={`px-1 rounded ${s.kind === "legal" ? "bg-black/10" : "bg-taven-gold/20"}`}>{s.kind === "legal" ? "Legal Policy" : s.kind === "program" ? "Program Info" : "Account"}</span>
                        {s.draft_warning && <span className="text-amber-700 font-bold">DRAFT — NOT PUBLISHED</span>}
                      </div>
                    ))}
                  </div>
                )}
                {m.role === "guide" && m.escalated && (
                  <div className="mt-1.5 text-[11px] text-orange-700 flex items-center gap-1" data-testid="yeda-escalated"><LifeBuoy className="w-3 h-3" /> Passed to the TAVEN team — a person will follow up.</div>
                )}
                {m.role === "guide" && m.conversation_id && !m.rated && (
                  <div className="mt-1 flex gap-2 text-[11px]" data-testid="yeda-feedback">
                    <button onClick={() => { yedaFeedback(m.conversation_id, true); setMsgs((x) => x.map((y, k) => k === i ? { ...y, rated: true } : y)); }} className="text-black/40 hover:text-green-600">Helpful</button>
                    <button onClick={() => { yedaFeedback(m.conversation_id, false); setMsgs((x) => x.map((y, k) => k === i ? { ...y, rated: true } : y)); }} className="text-black/40 hover:text-red-600">Not helpful</button>
                  </div>
                )}
              </div>
            ))}
            {busy && <div className="text-xs text-black/40" data-testid="yeda-typing">YEDA is checking approved sources…</div>}
          </div>

          <div className="border-t border-black/10 p-2 flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about TAVEN…" className="flex-1 px-3 py-2 text-sm outline-none" data-testid="yeda-input" aria-label="Ask YEDA" />
            <button onClick={send} disabled={busy} className="bg-taven-black text-white p-2 rounded hover:bg-taven-orange disabled:opacity-50" data-testid="yeda-send" aria-label="Send"><Send className="w-4 h-4" /></button>
          </div>
          <p className="text-[10px] text-black/30 px-3 pb-2">Guidance only — not legal, tax or financial advice. TAVEN does not guarantee earnings.</p>
        </div>
      )}
    </>
  );
}
